import styles from "./styles/AccordionTerrace.module.scss";
import Markdown from "../../../utilities/Markdown";
import FontAwesome from "../../../utilities/FontAwesome";
import SchoolBlocksLoadingIndicator from "../../../utilities/SchoolBlocksLoadingIndicator";
import { useStores } from "../../../../stores/useStores";
import { AccordionItemViewMoreButton } from "./AccordionItem";
import {
  AccordionItemPayload,
  ReactiveAccordionItem,
} from "../../../schoolfeed/ReactiveAccordionItem";

// the search endpoint merges the accordion item serializer onto each hit, so a result is an
// accordion item payload plus the search-specific fields
type SearchResult = AccordionItemPayload & {
  headline: string;
  terrace: ITerrace;
  breadcrumbs: string;
};

interface AccordionItemSearchResultProps {
  result: SearchResult;
  index: number;
  results: SearchResult[];
  blockObj: IAccordionTerrace;
}

const AccordionItemSearchResult: React.FC<AccordionItemSearchResultProps> = ({
  result,
  index,
  results,
  blockObj,
}) => {
  const { modalStore } = useStores();

  // the search endpoint returns plain JSON, so every item handed to the modal has to be
  // constructed here - the modal writes fetched content back with ReactiveAccordionItem.update()
  const getItem = (idx) => {
    return results[idx] ? new ReactiveAccordionItem(results[idx]) : null;
  };

  const onClick = (e) => {
    e.stopPropagation();
    modalStore.addModal({
      type: "accordion",
      itemObj: new ReactiveAccordionItem(result),
      blockObj: blockObj,
      totalItemCount: results.length,
      initialItemIndex: index,
      getItemAtIndex: getItem,
    });
  };
  return (
    <button onClick={onClick} className={styles.accordionSearchResult}>
      <h4 className="accordion-title">{result.title}</h4>
      <div className={styles.breadcrumbs}>{result.breadcrumbs}</div>
      <Markdown withStyles={true} source={result.headline} />
      <AccordionItemViewMoreButton />
    </button>
  );
};

interface AccordionItemSearchResultListProps {
  results: SearchResult[];
  setIsSearching: React.Dispatch<React.SetStateAction<boolean>>;
  searchTerm: string;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  previousSearchTerm: string;
  isLoading: boolean;
  blockObj: IAccordionTerrace;
}

const AccordionItemSearchResultList: React.FC<
  AccordionItemSearchResultListProps
> = ({
  results,
  setIsSearching,
  searchTerm,
  setIsExpanded,
  setSearchTerm,
  previousSearchTerm,
  isLoading,
  blockObj,
}) => {
  if (isLoading)
    return (
      <div className={styles.accordionSearchContainer}>
        <div className={styles.accordionSearchLoading}>
          <h3 className={styles.searchResultsTitle}>
            Searching for &apos;{searchTerm}&apos;...
          </h3>
          <SchoolBlocksLoadingIndicator />
        </div>
      </div>
    );
  else {
    return (
      <div className={styles.accordionSearchContainer}>
        <div className={styles.accordionSearchHeader}>
          <div className={styles.goBackWrapper}>
            <button
              className={styles.goBackButton}
              onClick={() => {
                setIsSearching(false);
                setIsExpanded(false);
                setSearchTerm("");
              }}
            >
              <FontAwesome prefix={"fas"} name={"fa-arrow-left"} />
              &nbsp;Go Back
            </button>
          </div>
          <h3 className={styles.searchResultsTitle}>
            {results.length} Search Result{results.length !== 1 && "s"} for
            &apos;{previousSearchTerm}&apos;
          </h3>
        </div>

        {results.length > 0 &&
          results.map((result, index) => (
            <AccordionItemSearchResult
              key={result.id}
              index={index}
              result={result}
              results={results}
              blockObj={blockObj}
            />
          ))}
      </div>
    );
  }
};

export { AccordionItemSearchResultList, AccordionItemSearchResult };
